import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Button,
  Divider,
  Badge,
  Popover,
  List,
  ListItemButton,
  ListItemText,
  Tooltip,
} from '@mui/material';
import { IoLanguageOutline } from 'react-icons/io5';
import { PiUserCircleThin } from 'react-icons/pi';
//import SettingsIcon from '@mui/icons-material/Settings';
import PatternIcon from '@mui/icons-material/Pattern';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import LogoutIcon from '@mui/icons-material/Logout';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { hasActionPermission } from '../utils/accessControl';
import './../App.css';

const Header = ({ sidebarExpanded, toggleSidebar, setIsAuthenticated }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [languageAnchorEl, setLanguageAnchorEl] = useState(null);
  const [userMenuAnchorEl, setUserMenuAnchorEl] = useState(null);
  const [userName, setUserName] = useState('');
  const [userId, setUserId] = useState('');

  useEffect(() => {
    const storedUserName = localStorage.getItem('userName');
    const storedUserId = localStorage.getItem('userId');
    if (storedUserName && storedUserId) {
      setUserName(storedUserName);
      setUserId(storedUserId);
    }
  }, []);

  // جرس التنبيهات (بالون) بجانب زر اللغة — يظهر لمن يملك صلاحية notifications.
  const canSeeNotifs = hasActionPermission('notifications');
  const [notifAnchor, setNotifAnchor] = useState(null);
  const [notifItems, setNotifItems] = useState([]);
  const [notifCount, setNotifCount] = useState(0);

  useEffect(() => {
    if (!canSeeNotifs) return undefined;
    let alive = true;
    api.get('/notifications/count').then((r) => { if (alive) setNotifCount(r.data?.count || 0); }).catch(() => {});
    return () => { alive = false; };
  }, [canSeeNotifs]);

  const openNotifs = async (event) => {
    setNotifAnchor(event.currentTarget);
    try {
      const r = await api.get('/notifications');
      setNotifItems(r.data?.items || []);
      setNotifCount(r.data?.total || 0);
    } catch (_) { /* تجاهل */ }
  };

  const handleLanguageClick = (event) => {
    setLanguageAnchorEl(event.currentTarget);
  };

  const handleLanguageChange = (lang) => {
    i18n.changeLanguage(lang);
    setLanguageAnchorEl(null);
    document.body.dir = lang === 'ar' ? 'rtl' : 'ltr';
  };

  const handleUserMenuClick = (event) => {
    setUserMenuAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchorEl(null);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userName');
    localStorage.removeItem('userId');
    localStorage.removeItem('userRole');
    localStorage.removeItem('role_id');
    localStorage.removeItem('owner_id');
    localStorage.removeItem('permissions');
    localStorage.removeItem('me_menu');
    setIsAuthenticated(false);

    navigate('/login');
  };

  return (
    <Box
      className="header-container"
      sx={{
        backgroundColor: '#1a468d', // أزرق ملكي حسب الهوية
        color: '#fff',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        px: 2,
        direction: i18n.language === 'ar' ? 'rtl' : 'ltr', // اجعل الاتجاه صحيح حسب اللغة
        minHeight: '56px',
      }}
    >
      <Typography variant="h6">{/*Smart Stable*/}</Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {/* المساعدة: علامة صغيرة قبل الجرس، متاحة من كلّ شاشة وبلا صلاحية — الصفحة شرح
            لا بيانات. */}
        <Tooltip title={i18n.language === 'ar' ? 'دليل استخدام النظام' : 'System user guide'}>
          <IconButton onClick={() => navigate('/Help')} size="small" sx={{ color: '#000' }} aria-label="help">
            <HelpOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {canSeeNotifs ? (
          <>
            <IconButton onClick={openNotifs} sx={{ color: '#000' }} aria-label="notifications">
              <Badge badgeContent={notifCount} color="error" max={99}>
                <NotificationsNoneIcon />
              </Badge>
            </IconButton>
            <Popover
              open={Boolean(notifAnchor)}
              anchorEl={notifAnchor}
              onClose={() => setNotifAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
              transformOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
              <Box sx={{ width: 330, maxHeight: 400, overflowY: 'auto', direction: i18n.language === 'ar' ? 'rtl' : 'ltr' }}>
                <Box sx={{ px: 2, py: 1, fontWeight: 900 }}>{t('notifications') || 'التنبيهات'} ({notifCount})</Box>
                <Divider />
                {notifItems.length === 0 ? (
                  <Box sx={{ p: 2, color: '#777' }}>{t('no_notifications') || 'لا تنبيهات'}</Box>
                ) : (
                  <List dense disablePadding>
                    {notifItems.slice(0, 15).map((n, i) => (
                      <ListItemButton key={i} onClick={() => { setNotifAnchor(null); if (n.link) navigate(n.link); }}
                        sx={{ borderInlineStart: `3px solid ${n.severity === 'error' ? '#c62828' : n.severity === 'warning' ? '#b8860b' : '#1a468d'}` }}>
                        <ListItemText primary={n.title} secondary={n.detail} primaryTypographyProps={{ fontWeight: 700, fontSize: 13 }} secondaryTypographyProps={{ fontSize: 12 }} />
                      </ListItemButton>
                    ))}
                  </List>
                )}
                <Divider />
                <Button fullWidth size="small" onClick={() => { setNotifAnchor(null); navigate('/notifications'); }}>
                  {t('view_all') || 'عرض الكل'}
                </Button>
              </Box>
            </Popover>
          </>
        ) : null}
        <IconButton onClick={handleLanguageClick}>
          <IoLanguageOutline />
        </IconButton>
        <Menu
          anchorEl={languageAnchorEl}
          open={Boolean(languageAnchorEl)}
          onClose={() => setLanguageAnchorEl(null)}
        >
          <MenuItem onClick={() => handleLanguageChange('en')}>EN</MenuItem>
          <MenuItem onClick={() => handleLanguageChange('ar')}>AR</MenuItem>
          <MenuItem onClick={() => handleLanguageChange('pt')}>PT</MenuItem>
        </Menu>

        <IconButton onClick={handleUserMenuClick}>
          <PiUserCircleThin size={28} />
        </IconButton>
        <Menu
          anchorEl={userMenuAnchorEl}
          open={Boolean(userMenuAnchorEl)}
          onClose={handleUserMenuClose}
          PaperProps={{ elevation: 4, sx: { borderRadius: 2, p: 1 } }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            px={2}
            py={1}
            textAlign="center"
          >
            <Typography
              variant="subtitle1"
              fontWeight="bold"
              textTransform="uppercase"
            >
              {userName}
            </Typography>
            <Typography variant="body2">{userId}</Typography>
          </Box>

          <Divider sx={{ my: 1 }} />

          <MenuItem
            onClick={() => {
              navigate('/ChangePassword');
              handleUserMenuClose();
            }}
          >
            <PatternIcon fontSize="small" style={{ marginRight: 8 }} />
            {t('Change_password')}
          </MenuItem>
          {/*}
          <MenuItem
            onClick={() => {
              navigate('/settings');
              handleUserMenuClose();
            }}
          >
            <SettingsIcon fontSize="small" sx={{ mr: 1 }} />
            {t('Settings')}
          </MenuItem>
          <MenuItem
            onClick={() => {
              navigate('/Help');
              handleUserMenuClose();
            }}
          >
            <HelpOutlineIcon fontSize="small" sx={{ mr: 1 }} />
            {t('FAQ')}
          </MenuItem>
*/}
          <Divider sx={{ my: 1 }} />
          <Box px={2} py={1}>
            <Button
              fullWidth
              variant="contained"
              color="error"
              startIcon={<LogoutIcon />}
              onClick={() => {
                handleLogout();
                handleUserMenuClose();
              }}
              sx={{ typography: 'body1' }}
            >
              {t('logout')}
            </Button>
          </Box>
        </Menu>
      </Box>
    </Box>
  );
};

export default Header;
